import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { post } from "@/lib/api";
import { cn } from "@/lib/utils";

const S = "h-7 bg-input border border-border rounded-sm text-[11px] px-1.5 min-w-0";

const TYPES = [
  { v: "sortie", label: "Sortie", perm: "inventory.issue" },
  { v: "transfert", label: "Transfert", perm: "inventory.transfer" },
  { v: "ajustement", label: "Ajustement (+/-)", perm: "inventory.adjust" },
  { v: "initialisation", label: "Stock initial", perm: "inventory.initialize" },
  { v: "retour", label: "Retour", perm: "inventory.receive" },
];

const VIDE = { type: "", produit_id: "", lot_id: "", quantite: "", motif: "", commentaire: "",
  reference_externe: "", emplacement_source_id: "", emplacement_dest_id: "" };

/** Saisie d'un mouvement : les emplacements exigés dépendent du type (règles backend identiques). */
export const MvtForm = ({ produits, emplacements, lots, motifs, perms, negatifAutorise }) => {
  const dispos = TYPES.filter((t) => perms.includes(t.perm));
  const [f, setF] = useState({ ...VIDE, type: dispos[0]?.v || "" });
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body) => post("/inventaire/mouvements", body),
    onSuccess: () => {
      toast.success("Mouvement enregistré");
      setF({ ...VIDE, type: f.type });
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e.message),
  });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const besoinSource = ["sortie", "transfert", "ajustement"].includes(f.type);
  const besoinDest = ["transfert", "initialisation", "retour"].includes(f.type);
  const manque = !f.produit_id || !f.quantite
    || (besoinSource && !f.emplacement_source_id)
    || (besoinDest && !f.emplacement_dest_id)
    || (f.type === "ajustement" && !f.commentaire)
    || (["sortie", "transfert"].includes(f.type) && !f.motif);
  const envoyer = () => m.mutate({
    ...f,
    produit_id: Number(f.produit_id),
    lot_id: f.lot_id ? Number(f.lot_id) : null,
    quantite: Number(f.quantite),
    emplacement_source_id: f.emplacement_source_id ? Number(f.emplacement_source_id) : null,
    emplacement_dest_id: f.emplacement_dest_id ? Number(f.emplacement_dest_id) : null,
  });
  const lotsProduit = lots.filter((l) => !f.produit_id || l.produit_id === Number(f.produit_id));
  const Empl = ({ k, label, testId }) => (
    <label className="flex flex-col gap-0.5">
      <span className="eyebrow">{label}</span>
      <select data-testid={testId} className={S} value={f[k]} onChange={up(k)}>
        <option value="">Choisir…</option>
        {emplacements.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
      </select>
    </label>
  );
  if (!dispos.length) return null;
  return (
    <div className="space-y-2" data-testid="mvt-form">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">Type</span>
          <select data-testid="mvt-type" className={S} value={f.type}
            onChange={(e) => setF({ ...VIDE, type: e.target.value })}>
            {dispos.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">Produit</span>
          <select data-testid="mvt-produit" className={S} value={f.produit_id} onChange={up("produit_id")}>
            <option value="">Produit…</option>
            {produits.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">Lot (option)</span>
          <select data-testid="mvt-lot" className={S} value={f.lot_id} onChange={up("lot_id")}>
            <option value="">—</option>
            {lotsProduit.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">{f.type === "ajustement" ? "Quantité (+/-)" : "Quantité"}</span>
          <input data-testid="mvt-quantite" className={cn(S, "w-24 font-mono")} placeholder="0"
            value={f.quantite} onChange={up("quantite")} />
        </label>
        {besoinSource && <Empl k="emplacement_source_id" label={f.type === "ajustement" ? "Emplacement" : "Depuis"} testId="mvt-source" />}
        {besoinDest && <Empl k="emplacement_dest_id" label="Vers" testId="mvt-dest" />}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">Motif</span>
          {f.type === "sortie" ? (
            <select data-testid="mvt-motif" className={cn(S, "w-44")} value={f.motif} onChange={up("motif")}>
              <option value="">Motif…</option>
              {motifs.map((mo) => <option key={mo} value={mo}>{mo.replace(/_/g, " ")}</option>)}
            </select>
          ) : (
            <input data-testid="mvt-motif" className={cn(S, "w-44")} placeholder="Motif" value={f.motif} onChange={up("motif")} />
          )}
        </label>
        <label className="flex flex-col gap-0.5 flex-1 min-w-[180px]">
          <span className="eyebrow">Commentaire{f.type === "ajustement" ? " (obligatoire)" : ""}</span>
          <input data-testid="mvt-commentaire" className={S} placeholder="Traçabilité" value={f.commentaire} onChange={up("commentaire")} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="eyebrow">Référence</span>
          <input data-testid="mvt-reference" className={cn(S, "w-36")} placeholder="BL, commande…"
            value={f.reference_externe} onChange={up("reference_externe")} />
        </label>
        <button data-testid="mvt-submit" disabled={m.isPending || manque} onClick={envoyer}
          className="h-7 px-3 bg-primary text-primary-foreground rounded-sm text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <Save className="h-3.5 w-3.5" />Enregistrer
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Tout mouvement est historisé (qui, quand, motif, emplacement).
        {negatifAutorise ? "" : " Stock négatif interdit pour cette entreprise."}
      </p>
    </div>
  );
};
